import React from 'react';
import { AxiosError } from 'axios';
import { useJobApplications } from '../use-job-applications/useJobApplications';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import { JobApplicationNoticeData } from '../../../providers/api/dtos/notice';
import { SortOrder } from '../../../providers/pagination';
import { JobApplication } from '../../../providers/api/dtos';
import { useSimilarApplicants } from '../../../applicant/hooks/use-similar-applicants/useSimilarApplicants';
import { JobApplicationStatus } from '../../../domain/types';

export type UseJobApplicationNoticesDataResult = {
  data?: JobApplicationNoticeData[],
  isLoading: boolean,
  errors?: Error[] | undefined,
  mutate: VoidFunction,
};

const useJobApplicationNoticesData = (jobApplication?: JobApplication): UseJobApplicationNoticesDataResult => {
  const [errors, setErrors] = React.useState<AxiosError[] | undefined>();

  const { userIsAdmin, userIsHR } = useRBAC();

  const handleApiError = React.useCallback((err: AxiosError) => {
    if (err.response?.status === 404) { return; }

    setErrors((prev) => {
      if (!prev) return [err];
      return [...prev, err];
    });
  }, []);

  const {
    data: jobApplicationsOfSameApplicant,
    isLoading: isOthersJobApplicationsLoading,
    mutate: mutateJobApplicationsOfSameApplicant,
  } = useJobApplications({ applicantId: jobApplication?.applicantId }, { sort: { updatedAt: SortOrder.DESC } }, { onError: handleApiError });

  const {
    data: similarApplicants,
    isLoading: areSimilarApplicantsLoading,
    mutate: mutateSimilarApplicants,
  } = useSimilarApplicants(jobApplication?.applicantId, { onError: handleApiError });

  const {
    data: jobApplicationsOfSimilarApplicants,
    isLoading: areJobApplicationsOfSimilarApplicantsLoading,
    mutate: mutateJobApplicationsOfSimilarApplicants,
  } = useJobApplications(similarApplicants && similarApplicants.length > 0 ? {
    applicantId: similarApplicants.map(({ id }) => id!),
  } : null, { sort: { updatedAt: SortOrder.DESC } }, { onError: handleApiError });

  const isLoading = React.useMemo(
    () => (
      isOthersJobApplicationsLoading
      || areSimilarApplicantsLoading
      || areJobApplicationsOfSimilarApplicantsLoading
    ),
    [areJobApplicationsOfSimilarApplicantsLoading, areSimilarApplicantsLoading, isOthersJobApplicationsLoading],
  );

  const data = React.useMemo(() => {
    const noticesData: JobApplicationNoticeData[] = [];
    const canViewDuplicateNotices = (userIsAdmin || userIsHR);

    const normalizedJobApplicationsOfSameApplicant = jobApplication
      && jobApplicationsOfSameApplicant?.content
        .filter(({ status }) => status !== JobApplicationStatus.KO_DUPLICATE)
        .filter(({ id }) => id !== jobApplication.id);

    const normalizedJobApplicationsOfSimilarApplicants = jobApplicationsOfSimilarApplicants?.content
      .filter(({ status }) => status !== JobApplicationStatus.KO_DUPLICATE);

    const hasSameApplicantJobApplications = (normalizedJobApplicationsOfSameApplicant?.length ?? 0) > 0;
    const hasSimilarApplicantsJobApplications = (normalizedJobApplicationsOfSimilarApplicants?.length ?? 0) > 0;

    if (jobApplication && canViewDuplicateNotices && (hasSameApplicantJobApplications || hasSimilarApplicantsJobApplications)) {
      noticesData.push({
        type: 'JOB_APPLICATION_DUPLICATES',
        referenceId: jobApplication.id!,
        data: {
          sameApplicantJobApplications: normalizedJobApplicationsOfSameApplicant ?? [],
          similarApplicants: similarApplicants ?? [],
          similarApplicantsJobApplications: normalizedJobApplicationsOfSimilarApplicants ?? [],
        },
      });
    }

    return noticesData;
  }, [jobApplication, jobApplicationsOfSameApplicant, jobApplicationsOfSimilarApplicants, similarApplicants, userIsAdmin, userIsHR]);

  const mutate = React.useCallback(() => {
    mutateJobApplicationsOfSameApplicant();
    mutateSimilarApplicants();
    mutateJobApplicationsOfSimilarApplicants();
  }, [mutateJobApplicationsOfSameApplicant, mutateJobApplicationsOfSimilarApplicants, mutateSimilarApplicants]);

  return {
    data,
    isLoading,
    errors,
    mutate,
  };
};

export default useJobApplicationNoticesData;
